import React from 'react'
import {
  Truck,
  MapPin,
  Package,
  PackageCheck,
  Route,
  Warehouse,
  Navigation,
  ShieldCheck,
  Activity,
  Network,
  ArrowRight,
  MoveRight,
  CheckCircle2,
  Boxes,
  ClipboardCheck
} from 'lucide-react'

import { motion } from 'framer-motion'
import { Helmet } from 'react-helmet-async'

import lastMileImage from '../assets/images/data/new_hero_mobile.png'
import CTA from './CTA'
import IndustryExpertise from './IndustryExpertise'
import MoreServicesSection from './MoreServicesSection'

const services = [
  {
    number: '01',
    icon: Package,
    title: 'First-Mile Pickup',
    description:
      'Efficient pickup coordination from business locations, warehouses and distribution centres to initiate your shipment journey.'
  },
  {
    number: '02',
    icon: Truck,
    title: 'Last-Mile Delivery',
    description:
      'Coordinated delivery solutions connecting distribution hubs with final destinations and customers.'
  },
  {
    number: '03',
    icon: Network,
    title: 'Multimodal Transportation',
    description:
      'Integrated road, rail, air and sea transportation options to support diverse cargo movement requirements.'
  },
  {
    number: '04',
    icon: Warehouse,
    title: 'Distribution & Hub Management',
    description:
      'Organized shipment movement through distribution centres, logistics hubs and transit points.'
  },
  {
    number: '05',
    icon: Navigation,
    title: 'Route Optimization',
    description:
      'Planned transportation routes and delivery coordination designed to support efficient shipment movement.'
  },
  {
    number: '06',
    icon: Boxes,
    title: 'Business Distribution',
    description:
      'Flexible distribution support for businesses managing shipments across multiple locations and delivery points.'
  }
]

const benefits = [
  {
    icon: MapPin,
    title: 'Extensive Delivery Reach',
    description:
      'Connect business locations, distribution hubs and final destinations through coordinated logistics networks.'
  },
  {
    icon: Activity,
    title: 'Shipment Tracking Visibility',
    description:
      'Access shipment movement updates and tracking visibility throughout the transportation journey.'
  },
  {
    icon: Route,
    title: 'Efficient Route Planning',
    description:
      'Coordinate suitable routes and transportation options around shipment requirements and delivery schedules.'
  },
  {
    icon: ShieldCheck,
    title: 'Secure Shipment Handling',
    description:
      'Structured shipment handling and coordinated logistics processes designed to support cargo safety.'
  }
]

const process = [
  {
    number: '01',
    icon: Package,
    title: 'Shipment Pickup',
    description:
      'Coordinate shipment pickup from your business, warehouse or designated collection location.'
  },
  {
    number: '02',
    icon: ClipboardCheck,
    title: 'Network & Mode Planning',
    description:
      'Evaluate suitable transportation modes, delivery routes and transit connections for your shipment.'
  },
  {
    number: '03',
    icon: Truck,
    title: 'Transit & Distribution',
    description:
      'Coordinate shipment movement across transportation networks and distribution hubs.'
  },
  {
    number: '04',
    icon: PackageCheck,
    title: 'Final-Mile Delivery',
    description:
      'Coordinate final delivery to the intended destination with appropriate last-mile support.'
  }
]

const capabilities = [
  {
    icon: Package,
    title: 'First-Mile Pickup',
    description: 'Collection from businesses, warehouses and pickup points.'
  },
  {
    icon: Network,
    title: 'Multimodal Connectivity',
    description: 'Integrated road, rail, air and sea transportation.'
  },
  {
    icon: Warehouse,
    title: 'Distribution Network',
    description: 'Efficient movement through hubs and distribution centres.'
  },
  {
    icon: Truck,
    title: 'Last-Mile Delivery',
    description: 'Reliable delivery to customers and business destinations.'
  }
]

export default function LastMileMultimodalPage () {
  return (
    <>
      <Helmet>
        <title>Last-Mile & Multimodal Delivery | S Infinite Express</title>

        <meta
          name='description'
          content='Explore last-mile delivery and multimodal logistics solutions from S Infinite Express, including first-mile pickup, road and rail connectivity, distribution coordination and final-mile delivery support.'
        />

        <meta
          name='keywords'
          content='Last Mile Delivery, First Mile Pickup, Multimodal Transportation, Logistics India, Distribution Services, Road Transportation, Rail Logistics, Air Cargo, Sea Freight, Supply Chain Logistics, S Infinite Express'
        />

        <meta name='robots' content='index, follow' />

        <link
          rel='canonical'
          href='https://sinfiniteexpress.com/services/last-mile-multimodal-delivery'
        />

        <meta
          property='og:title'
          content='Last-Mile & Multimodal Delivery | S Infinite Express'
        />

        <meta
          property='og:description'
          content='Connect every stage of your supply chain with first-mile pickup, multimodal transportation, distribution and last-mile delivery solutions.'
        />

        <meta
          property='og:url'
          content='https://sinfiniteexpress.com/services/last-mile-multimodal-delivery'
        />

        <meta property='og:type' content='website' />

        <meta
          property='og:image'
          content='https://sinfiniteexpress.com/static/media/last-mile.png'
        />

        <meta property='og:site_name' content='S Infinite Express' />
      </Helmet>

      <main className='overflow-hidden bg-white'>
        {/* HERO SECTION */}

        <section className='relative overflow-hidden bg-gradient-to-br from-white via-blue-50/40 to-white'>
          <div className='max-w-7xl mx-auto px-5 sm:px-8 lg:px-6 py-8 md:py-12'>
            <div className='grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16 items-center'>
              {/* LEFT CONTENT */}

              <motion.div
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.7 }}
                className='space-y-6'
              >
                <h1 className='text-2xl sm:text-3xl lg:text-4xl xl:text-5xl font-extrabold text-[#211f57] leading-tight'>
                  Last-Mile
                  <span className='text-[#d32c30]'>
                    {' '}
                    & Multi-Modal Logistics
                  </span>
                </h1>

                <div className='w-24 h-1.5 bg-[#d32c30] rounded-full' />

                <h2 className='text-xl sm:text-2xl font-bold text-[#211f57]'>
                  Connecting Every Mile. Delivering Every Promise.
                </h2>

                <p className='text-gray-600 text-base sm:text-lg leading-relaxed'>
                  <span className='font-bold text-[#d32c30]'>
                    S INFINITE EXPRESS
                  </span>{' '}
                  helps businesses connect their supply chains through
                  coordinated first-mile pickup, multimodal transportation,
                  distribution and last-mile delivery solutions.
                </p>

                <p className='text-gray-600 leading-relaxed'>
                  From initial collection to final delivery, we support
                  connected cargo movement through suitable transportation
                  networks and coordinated logistics operations.
                </p>

                {/* ACTION BUTTONS */}

                <div className='flex flex-col sm:flex-row gap-4 pt-3'>
                  <a
                    href='/get-a-quote'
                    className='inline-flex items-center justify-center gap-3 px-7 py-4 bg-[#d32c30] text-white font-semibold rounded-xl shadow-lg hover:bg-[#b82629] hover:-translate-y-1 transition-all duration-300'
                  >
                    Get a Logistics Quote
                    <MoveRight className='w-5 h-5' />
                  </a>

                  <a
                    href='#rail-services'
                    className='inline-flex items-center justify-center gap-3 px-7 py-4 border-2 border-[#211f57] text-[#211f57] font-semibold rounded-xl hover:bg-[#211f57] hover:text-white transition-all duration-300'
                  >
                    Explore Services
                    <ArrowRight className='w-5 h-5' />
                  </a>
                </div>

                {/* TRUST POINTS */}

                <div className='flex flex-wrap gap-x-6 gap-y-3 pt-5 text-sm font-medium text-gray-600'>
                  {[
                    'First-Mile Pickup',
                    'Multimodal Connectivity',
                    'Last-Mile Delivery'
                  ].map(item => (
                    <span key={item} className='flex items-center gap-2'>
                      <CheckCircle2 className='w-4 h-4 text-green-600 shrink-0' />
                      {item}
                    </span>
                  ))}
                </div>
              </motion.div>

              {/* RIGHT IMAGE */}

              <motion.div
                initial={{ opacity: 0, scale: 0.94, x: 30 }}
                animate={{ opacity: 1, scale: 1, x: 0 }}
                transition={{ duration: 0.8 }}
                className='relative'
              >
                <div className='absolute -top-6 -right-5 w-32 h-32 bg-[#fff44f]/60 rounded-full blur-3xl' />

                <div className='absolute -bottom-6 -left-5 w-32 h-32 bg-blue-200/70 rounded-full blur-3xl' />

                <div className='relative p-2 bg-white rounded-3xl shadow-2xl border border-gray-100'>
                  <img
                    src={lastMileImage}
                    alt='S Infinite Express multimodal transportation and last-mile delivery logistics'
                    className='w-full h-[200px] sm:h-[300px] lg:h-[420px] object-cover rounded-2xl'
                    loading='lazy'
                    decoding='async'
                  />
                </div>
              </motion.div>
            </div>
          </div>
        </section>

        {/* INTRODUCTION SECTION */}

        <section className='relative overflow-hidden bg-gradient-to-br from-yellow-50 via-blue-50/40 to-yellow-100/60 py-8 sm:py-10 lg:py-12'>
          <div className='absolute -top-20 -left-20 h-64 w-64 rounded-full bg-[#fff44f]/10 blur-[90px]' />

          <div className='absolute -bottom-20 -right-20 h-64 w-64 rounded-full bg-blue-200/30 blur-[90px]' />

          <div className='relative mx-auto max-w-7xl px-5 sm:px-8 lg:px-12'>
            <div className='grid grid-cols-1 items-center gap-12 lg:grid-cols-2 lg:gap-16'>
              {/* LEFT CONTENT */}

              <motion.div
                initial={{ opacity: 0, x: -30 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.7 }}
                className='space-y-6'
              >
                <h2 className='text-2xl font-bold leading-tight text-[#211f57] sm:text-2xl lg:text-3xl'>
                  Connected Logistics for
                  <span className='text-[#d32c30]'>
                    {' '}
                    Seamless First-to-Last-Mile Delivery
                  </span>
                </h2>

                <p className='text-base leading-relaxed text-gray-600 sm:text-lg'>
                  At{' '}
                  <span className='font-bold text-[#d32c30]'>
                    S INFINITE EXPRESS
                  </span>
                  , we understand that efficient delivery requires coordinated
                  pickup, suitable transportation planning and reliable
                  destination support.
                </p>

                <p className='leading-relaxed text-gray-600'>
                  Our logistics solutions connect business locations,
                  transportation networks, distribution hubs and final
                  destinations through coordinated multimodal operations.
                </p>

                <div className='grid grid-cols-1 gap-x-5 gap-y-4 pt-3 sm:grid-cols-2'>
                  {[
                    'First-Mile Pickup',
                    'Multimodal Transportation',
                    'Distribution Coordination',
                    'Last-Mile Delivery'
                  ].map(item => (
                    <div key={item} className='flex items-center gap-3'>
                      <CheckCircle2
                        size={20}
                        className='shrink-0 text-green-600'
                      />

                      <span className='text-sm font-semibold text-[#211f57]'>
                        {item}
                      </span>
                    </div>
                  ))}
                </div>
              </motion.div>

              {/* RIGHT CAPABILITIES CARD */}

              <motion.div
                initial={{ opacity: 0, scale: 0.94, x: 30 }}
                whileInView={{ opacity: 1, scale: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.8 }}
                className='relative'
              >
                <div className='absolute -right-5 -top-6 h-32 w-32 rounded-full bg-[#fff44f]/50 blur-3xl' />

                <div className='absolute -bottom-6 -left-5 h-32 w-32 rounded-full bg-blue-200/70 blur-3xl' />

                <div className='relative overflow-hidden rounded-3xl bg-[#211f57] p-3 shadow-2xl sm:p-4 lg:p-5'>
                  <div className='absolute -right-16 -top-16 h-48 w-48 rounded-full border-[25px] border-white/5' />

                  <div className='absolute -bottom-20 -right-10 h-56 w-56 rounded-full border-[25px] border-white/5' />

                  <div className='relative z-10'>
                    <span className='text-xs font-bold uppercase tracking-[2px] text-[#fff44f]'>
                      Our Capabilities
                    </span>

                    <p className='mb-4 mt-3 text-sm leading-7 text-white/70 sm:text-base'>
                      Connected pickup, transportation and delivery coordination
                      designed to support efficient cargo movement from origin
                      to destination.
                    </p>

                    <div className='space-y-2'>
                      {capabilities.map(item => {
                        return (
                          <div
                            key={item.title}
                            className='flex items-center gap-4 rounded-xl border border-white/10 bg-white/5 p-4 transition-all duration-300 hover:bg-white/10'
                          >
                            <div>
                              <h4 className='text-sm font-bold text-white sm:text-base'>
                                {item.title}
                              </h4>

                              <p className='mt-1 text-xs leading-5 text-white/55 sm:text-sm'>
                                {item.description}
                              </p>
                            </div>
                          </div>
                        )
                      })}
                    </div>
                  </div>
                </div>
              </motion.div>
            </div>
          </div>
        </section>

        {/* LAST-MILE & MULTIMODAL SERVICES */}

        <section id='rail-services' className='py-5 bg-[#f7f8fc] scroll-mt-24'>
          <div className='max-w-7xl mx-auto px-6'>
            <div className='max-w-4xl mx-auto text-center mb-5'>
              <span className='text-[#d32c30] font-extrabold text-xs uppercase tracking-[2px]'>
                What We Offer
              </span>

              <h2 className='text-3xl sm:text-4xl font-extrabold text-[#211f57]'>
                Our Last-Mile & Multimodal Services
              </h2>

              <p className='text-gray-500 leading-7'>
                Integrated pickup, transportation and delivery solutions
                designed around your cargo and business requirements.
              </p>
            </div>

            <div className='grid sm:grid-cols-2 lg:grid-cols-3 gap-6'>
              {services.map((service, index) => {
                const Icon = service.icon

                return (
                  <motion.div
                    key={service.number}
                    initial={{ opacity: 0, y: 25 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{
                      duration: 0.5,
                      delay: index * 0.08
                    }}
                    className='group bg-white p-7 rounded-2xl border border-gray-100 hover:border-[#d32c30]/30 hover:shadow-xl transition-all duration-300'
                  >
                    <div className='flex justify-between items-center'>
                      <div className='w-14 h-14 rounded-xl bg-[#211f57] group-hover:bg-[#d32c30] flex items-center justify-center text-white transition-colors'>
                        <Icon size={26} />
                      </div>

                      <span className='text-3xl font-black text-gray-100 group-hover:text-[#d32c30]/20 transition-colors'>
                        {service.number}
                      </span>
                    </div>

                    <h3 className='text-lg font-extrabold text-[#211f57] mt-3'>
                      {service.title}
                    </h3>

                    <p className='text-sm text-gray-500 leading-7 mt-2'>
                      {service.description}
                    </p>
                  </motion.div>
                )
              })}
            </div>
          </div>
        </section>

        {/* INDUSTRY EXPERTISE */}

        <IndustryExpertise />

        {/* WHY CHOOSE US */}

        <section className='relative overflow-hidden bg-gradient-to-br from-yellow-50 via-blue-50/40 to-yellow-100/60 py-8 sm:py-10 lg:py-12'>
          <div className='absolute -top-20 -left-20 h-64 w-64 rounded-full bg-[#fff44f]/10 blur-[90px]' />

          <div className='absolute -bottom-20 -right-20 h-64 w-64 rounded-full bg-blue-200/30 blur-[90px]' />

          <div className='relative max-w-7xl mx-auto px-6'>
            <div className='text-center max-w-4xl mx-auto mb-5'>
              <span className='text-[#d32c30] font-extrabold uppercase tracking-[2px] text-xs'>
                The S Infinite Advantage
              </span>

              <h2 className='text-3xl sm:text-4xl font-extrabold text-[#211f57]'>
                Why Choose Our Logistics Solutions?
              </h2>

              <p className='text-gray-500 leading-7'>
                Connected transportation, coordinated delivery and
                business-focused logistics support for every stage of your
                supply chain.
              </p>
            </div>

            <div className='grid sm:grid-cols-2 lg:grid-cols-4 gap-6'>
              {benefits.map((benefit, index) => {
                const Icon = benefit.icon

                return (
                  <motion.div
                    key={benefit.title}
                    initial={{ opacity: 0, y: 25 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{
                      duration: 0.5,
                      delay: index * 0.08
                    }}
                    className='group bg-white p-7 rounded-2xl border border-gray-100 hover:border-[#d32c30]/30 hover:shadow-xl transition-all duration-300'
                  >
                    <div className='w-14 h-14 rounded-xl bg-[#211f57] group-hover:bg-[#d32c30] flex items-center justify-center text-white transition-colors'>
                      <Icon size={26} />
                    </div>

                    <h3 className='text-lg font-extrabold text-[#211f57] mt-4'>
                      {benefit.title}
                    </h3>

                    <p className='text-sm text-gray-500 leading-7 mt-2'>
                      {benefit.description}
                    </p>
                  </motion.div>
                )
              })}
            </div>
          </div>
        </section>

        {/* PROCESS SECTION */}

        <section className='py-6 lg:py-8 bg-white'>
          <div className='max-w-7xl mx-auto px-6'>
            <div className='text-center max-w-4xl mx-auto mb-5'>
              <span className='text-[#d32c30] font-extrabold text-xs uppercase tracking-[2px]'>
                How It Works
              </span>

              <h2 className='text-2xl sm:text-3xl font-extrabold text-[#211f57]'>
                Our Last-Mile & Multimodal Delivery Process
              </h2>

              <p className='text-gray-500 leading-7'>
                A coordinated logistics process that connects pickup,
                transportation, distribution and final delivery.
              </p>
            </div>

            <div className='grid sm:grid-cols-2 lg:grid-cols-4 gap-8 relative'>
              {process.map((step, index) => {
                const Icon = step.icon

                return (
                  <motion.div
                    key={step.number}
                    initial={{ opacity: 0, y: 25 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{
                      duration: 0.5,
                      delay: index * 0.1
                    }}
                    className='relative text-center'
                  >
                    <div className='w-16 h-16 mx-auto rounded-2xl bg-[#211f57] text-white flex items-center justify-center shadow-lg'>
                      <Icon size={27} />
                    </div>

                    <span className='inline-block text-xs font-extrabold text-[#d32c30] mt-4'>
                      STEP {step.number}
                    </span>

                    <h3 className='text-lg font-extrabold text-[#211f57] mt-2'>
                      {step.title}
                    </h3>

                    <p className='text-sm text-gray-500 leading-7 mt-3'>
                      {step.description}
                    </p>

                    {index !== process.length - 1 && (
                      <div className='hidden lg:block absolute top-8 left-[calc(50%+45px)] w-[calc(100%-50px)] border-t-2 border-dashed border-[#d32c30]/30' />
                    )}
                  </motion.div>
                )
              })}
            </div>
          </div>
        </section>

        {/* FINAL CTA */}

        <section className='relative bg-[#211f57] py-4 overflow-hidden'>
          <div className='absolute right-0 top-0 w-96 h-96 rounded-full border-[50px] border-white/5 translate-x-1/3 -translate-y-1/3' />

          <div className='relative max-w-5xl mx-auto px-6 text-center'>
            <h2 className='text-2xl sm:text-3xl lg:text-3xl font-extrabold text-white leading-tight'>
              Looking for Connected Logistics Solutions?
              <span className='block text-[#FFF44F]'>
                Let's Connect Every Mile.
              </span>
            </h2>

            <p className='text-white/70 max-w-4xl mx-auto mt-4 leading-8'>
              Partner with S Infinite Express for coordinated first-mile pickup,
              multimodal transportation, distribution and last-mile delivery
              solutions tailored to your business.
            </p>
          </div>
        </section>

        {/* MORE SERVICES */}

        <MoreServicesSection />

        {/* COMMON CTA */}

        <CTA />
      </main>
    </>
  )
}
